// HR admins choose which libraries and folders on the HR SharePoint site Ask HR reads.
// Top: the current list (change who each applies to, or remove). Bottom: browse the site and
// add a library or folder. The API re-checks everything: role, site membership, overlap.

import { useEffect, useState } from "react";
import type { FolderListing, FolderRef, Library } from "@ask-hr/core/graph";
import {
  APPLICABILITY_LABELS,
  ROOT,
  coveringSource,
  type ApplicabilityValue,
  type SharePointSource,
} from "@ask-hr/core/sources";
import { ApiError, api } from "../api.js";

const APPLICABILITY = Object.entries(APPLICABILITY_LABELS) as [ApplicabilityValue, string][];

type Target = Pick<FolderRef, "driveId" | "driveName" | "itemId" | "name" | "path">;

export function SourcesPage() {
  const [sources, setSources] = useState<SharePointSource[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<string>("");
  const [applyTo, setApplyTo] = useState<ApplicabilityValue>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [replacing, setReplacing] = useState<{ target: Target; sources: SharePointSource[] } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const fetchSources = () => api<{ sources: SharePointSource[] }>("GET", "/api/admin/sources").then((r) => r.sources);

  const reload = async () => {
    try {
      setSources(await fetchSources());
      setError(null);
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    let live = true;
    fetchSources().then(
      (s) => live && setSources(s),
      (e: unknown) => live && setError(e),
    );
    return () => {
      live = false;
    };
  }, []);

  async function add(target: Target, replace = false) {
    const key = `${target.driveId}~${target.itemId}`;
    setBusy(key);
    setStatus("");
    try {
      await api("POST", "/api/admin/sources", { driveId: target.driveId, itemId: target.itemId, applicability: applyTo, replace });
      setReplacing(null);
      setStatus(`Added ${label(target)}.`);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "would_replace") {
        setReplacing({ target, sources: e.body.sources as SharePointSource[] });
      } else if (e instanceof ApiError && e.body.error === "already_included") {
        setStatus(`${label(target)} is already included.`);
        await reload();
      } else {
        setError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  async function setApplicability(s: SharePointSource, applicability: ApplicabilityValue) {
    setBusy(s.id);
    try {
      await api("PATCH", `/api/admin/sources/${encodeURIComponent(s.id)}`, { applicability });
      setStatus(`${label(s)} now applies to: ${APPLICABILITY_LABELS[applicability]}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function remove(s: SharePointSource) {
    setBusy(s.id);
    try {
      await api("DELETE", `/api/admin/sources/${encodeURIComponent(s.id)}`);
      setConfirmRemove(null);
      setStatus(`Removed ${label(s)}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <section className="panel" aria-labelledby="sources-h">
        <h2 id="sources-h">SharePoint sources</h2>
        <p className="sub">
          Ask HR reads only the libraries and folders listed here, from the HR SharePoint site. Folders inside them are
          included. New and changed documents wait for HR review before Ask HR uses them in answers. A document
          can apply to a different group than its source: set that on the Documents tab.
        </p>

        {error ? <ErrorNotice error={error} /> : null}
        <p className="status" role="status">
          {status}
        </p>

        {sources === null ? (
          error ? null : <p className="sub">Loading…</p>
        ) : sources.length === 0 ? (
          <p className="empty">No sources yet. Choose a library or folder below.</p>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Library</th>
                  <th scope="col">Folder</th>
                  <th scope="col">Applies to</th>
                  <th scope="col">Added</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.id}>
                    <td>{s.driveName}</td>
                    <td>{s.path === "/" ? <span className="muted">Whole library</span> : s.path}</td>
                    <td>
                      <select
                        aria-label={`Who ${label(s)} applies to`}
                        value={s.applicability}
                        disabled={busy === s.id}
                        onChange={(e) => void setApplicability(s, e.target.value as ApplicabilityValue)}
                      >
                        {APPLICABILITY.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="muted" title={s.addedBy}>
                      {new Date(s.addedAt).toLocaleDateString()} · {s.addedBy}
                    </td>
                    <td>
                      <div className="actions">
                        <a href={s.webUrl} target="_blank" rel="noreferrer">
                          Open in SharePoint
                        </a>
                        {confirmRemove === s.id ? (
                          <>
                            <button type="button" className="danger" disabled={busy === s.id} onClick={() => void remove(s)}>
                              Remove {label(s)}
                            </button>
                            <button type="button" onClick={() => setConfirmRemove(null)}>
                              Keep
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={() => setConfirmRemove(s.id)}>
                            Remove…
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {replacing ? (
        <section className="panel notice" role="alert">
          <p>
            <strong>{label(replacing.target)}</strong> contains {replacing.sources.length === 1 ? "a source" : "sources"}{" "}
            already on the list. Adding it replaces{" "}
            {replacing.sources.map((s, i) => (
              <span key={s.id}>
                {i ? ", " : ""}
                {label(s)} ({APPLICABILITY_LABELS[s.applicability]})
              </span>
            ))}
            , and everything in it will apply to: {APPLICABILITY_LABELS[applyTo]}.
          </p>
          <div className="actions start">
            <button type="button" className="primary" disabled={busy !== null} onClick={() => void add(replacing.target, true)}>
              Replace and add
            </button>
            <button type="button" onClick={() => setReplacing(null)}>
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      <Browser sources={sources ?? []} applyTo={applyTo} onApplyTo={setApplyTo} busy={busy} onAdd={(t) => void add(t)} />
    </>
  );
}

type Crumb = { driveId: string; driveName: string; itemId: string; name: string };

function Browser({
  sources,
  applyTo,
  onApplyTo,
  busy,
  onAdd,
}: {
  sources: SharePointSource[];
  applyTo: ApplicabilityValue;
  onApplyTo: (a: ApplicabilityValue) => void;
  busy: string | null;
  onAdd: (t: Target) => void;
}) {
  const [trail, setTrail] = useState<Crumb[]>([]);
  const [libraries, setLibraries] = useState<Library[] | null>(null);
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [error, setError] = useState<unknown>(null);
  const here = trail.at(-1);

  useEffect(() => {
    let live = true;
    const load = here
      ? api<FolderListing>(
          "GET",
          `/api/admin/sharepoint/folder?driveId=${encodeURIComponent(here.driveId)}&itemId=${encodeURIComponent(here.itemId)}`,
        ).then((l) => live && (setListing(l), setError(null)))
      : api<{ libraries: Library[] }>("GET", "/api/admin/sharepoint/libraries").then(
          (r) => live && (setLibraries(r.libraries), setError(null)),
        );
    load.catch((e: unknown) => live && setError(e));
    return () => {
      live = false;
    };
  }, [here]);

  const shown = here && listing?.folder.driveId === here.driveId && listing.folder.itemId === here.itemId ? listing : null;
  const loading = here ? !shown : !libraries;

  const status = (driveId: string, path: string, target: Target) => {
    const by = coveringSource(sources, driveId, path);
    if (by) {
      return (
        <span className="pill" title={by.path === path ? "On the list" : `Included through ${label(by)}`}>
          Included
        </span>
      );
    }
    const key = `${target.driveId}~${target.itemId}`;
    return (
      <button type="button" disabled={busy !== null} aria-label={`Add ${label(target)}`} onClick={() => onAdd(target)}>
        {busy === key ? "Adding…" : "Add"}
      </button>
    );
  };

  return (
    <section className="panel" aria-labelledby="browse-h">
      <h2 id="browse-h">Browse the HR site</h2>
      <div className="toolbar">
        <label>
          New sources apply to{" "}
          <select value={applyTo} onChange={(e) => onApplyTo(e.target.value as ApplicabilityValue)}>
            {APPLICABILITY.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      <nav className="crumbs" aria-label="Folder path">
        <button type="button" aria-current={trail.length === 0 ? "page" : undefined} onClick={() => setTrail([])}>
          HR site
        </button>
        {trail.map((c, i) => (
          <span key={`${c.driveId}~${c.itemId}`}>
            <span aria-hidden="true">›</span>{" "}
            <button
              type="button"
              aria-current={i === trail.length - 1 ? "page" : undefined}
              onClick={() => setTrail(trail.slice(0, i + 1))}
            >
              {c.name}
            </button>
          </span>
        ))}
      </nav>

      {error ? <ErrorNotice error={error} /> : null}

      {loading && !error ? <p className="sub">Loading…</p> : null}

      {!here && libraries && !error ? (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Library</th>
                <th scope="col">
                  <span className="sr-only">Status</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {libraries.length === 0 ? (
                <tr>
                  <td colSpan={2} className="muted">
                    Ask HR can't see any libraries on the HR site.
                  </td>
                </tr>
              ) : (
                libraries.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <button
                        type="button"
                        className="linkbtn"
                        onClick={() => setTrail([{ driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name }])}
                      >
                        {l.name}
                      </button>
                    </td>
                    <td className="right">
                      {status(l.id, "/", { driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name, path: "/" })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {here && shown && !error ? (
        <>
          <div className="toolbar">
            <span>
              <strong>{shown.folder.itemId === ROOT ? `${shown.folder.name} (whole library)` : shown.folder.name}</strong>
              <span className="muted">
                {" "}
                · {shown.files.supported} readable {shown.files.supported === 1 ? "file" : "files"} (PDF, Word)
                {shown.files.other ? `, ${shown.files.other} other` : ""} here
              </span>
            </span>
            {status(shown.folder.driveId, shown.folder.path, shown.folder)}
            <a href={shown.folder.webUrl} target="_blank" rel="noreferrer">
              Open in SharePoint
            </a>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Folder</th>
                  <th scope="col">Items</th>
                  <th scope="col">
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.folders.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted">
                      No folders inside.
                    </td>
                  </tr>
                ) : (
                  shown.folders.map((f) => (
                    <tr key={f.itemId}>
                      <td>
                        <button
                          type="button"
                          className="linkbtn"
                          onClick={() =>
                            setTrail([...trail, { driveId: f.driveId, driveName: f.driveName, itemId: f.itemId, name: f.name }])
                          }
                        >
                          {f.name}
                        </button>
                      </td>
                      <td className="muted">{f.childCount}</td>
                      <td className="right">{status(f.driveId, f.path, f)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

function label(s: Pick<Target, "driveName" | "path" | "name"> | SharePointSource): string {
  return s.path === "/" ? s.driveName : `${s.driveName}${s.path}`;
}

function ErrorNotice({ error }: { error: unknown }) {
  let text = "Something went wrong. Try again, and tell IT if it keeps happening.";
  if (error instanceof ApiError) {
    if (error.body.error === "sharepoint_not_configured") {
      text = "The SharePoint connection isn't set up yet. IT needs to add the Ask HR backend's credentials.";
    } else if (error.body.error === "not_on_hr_site") {
      text = "That folder isn't on the HR site any more. It may have been moved or deleted.";
    } else if (error.status === 403) {
      text = "You need the HR.Admin role to manage sources.";
    } else if (error.body.error === "sharepoint_error") {
      text = "SharePoint returned an error. Try again in a minute.";
    }
  }
  return (
    <p className="notice" role="alert">
      {text}
    </p>
  );
}
